import "./tile-fields.scss";

import * as React from "react";

import {cs} from "@common/react/chain-services";
import {consumeContext, provideContext} from "@common/react/context";
import {getPath} from "@common/utils/arr-path";
import {isEmpty} from "@common/utils/objects";
import {Expandable} from "../../../../../../../common/expandable/expandable";
import {InfoDialog} from "../../../../../../../common/info-dialog/info-dialog";
import {getTileActionsUpdated} from "../actions/get-tile-actions-updated";
import {getTileFields} from "./common/get-tile-fields";
import {hasDateGroupsConfiguration} from "./common/has-date-groups-configuration";
import {getLimitUpdated} from "./common/limit-sorting/limit/get-limit-updated";
import {Limit} from "./common/limit-sorting/limit/limit";
import {AxisSorting} from "./common/limit-sorting/sorting/axis/axis-sorting";
import {getAxisSortingUpdated, getAxisSortingUpdatedByLimit} from "./common/limit-sorting/sorting/axis/get-axis-sorting-updated";
import {getGroupSortingUpdated} from "./common/limit-sorting/sorting/group/get-group-sorting-updated";
import {GroupSorting} from "./common/limit-sorting/sorting/group/group-sorting";
import {MeasureFieldGroups} from "./common/measure-field-groups/measure-field-groups";
import {getColumnType} from "./common/select-field/select-field";
import {formToSingleFieldWell} from "./common/single-field-well/form-to-single-field-well";
import {SingleFieldWell} from "./common/single-field-well/single-field-well";
import {updateLimitSortingInTile, updateMeasureFieldsGroupsInTile, updateSingleFieldInTile} from "./common/update-tile";

export const VerticalBarFields = ({tileActions, isSubTile, debugFunction, tile, form, next}) =>
    cs(
        consumeContext("getFieldInfo"),
        [
            "tileFields",
            ({getFieldInfo}, next) =>
                next(
                    getTileFields({
                        tile: tile.value,
                        dimensionAttr: "xAxisField",
                        measureGroupAttrs: ["yAxisFields"],
                        groupFieldAttr: "groupField",
                        getFieldInfo,
                    })
                ),
        ],
        ({tileFields}, next) => provideContext("tileFields", tileFields, next),

        [
            "xAxisField",
            ({tileFields}, next) =>
                SingleFieldWell({
                    ...formToSingleFieldWell(form, ["xAxisField"]),
                    $type: "DimensionTileField",
                    onUpdateTile: (newVal, oriVal) =>
                        updateSingleFieldInTile({
                            fieldAttr: "xAxisField",
                            fieldVal: {newVal, oriVal},
                            tileFields,
                            tile,
                            updateSorting: getAxisSortingUpdated,
                            updateLimit: getLimitUpdated,
                            updateTileActions: getTileActionsUpdated,
                        }),
                    label: "X-Axis",
                    next,
                }),
        ],
        ["infoDialog", (_, next) => InfoDialog({next})],
        [
            "yAxisFields",
            ({tileFields, infoDialog}, next) =>
                (() => {
                    const formField = form.field(["yAxisFields"]);
                    return MeasureFieldGroups({
                        fieldGroups: formField.state,
                        hasError: formField.error,
                        domRef: formField.domRef,
                        label: "Y-Axis",
                        onUpdateTile: (newVal, indexes) =>
                            updateMeasureFieldsGroupsInTile({
                                fieldAttr: "yAxisFields",
                                fieldVal: {newVal, oriVal: null},
                                tileFields,
                                indexes,
                                tile,
                                updateSorting: getAxisSortingUpdated,
                                updateLimit: getLimitUpdated,
                            }),
                        next,
                        unavailable: () => !isEmpty(tile.value?.groupField),
                        showUnavailableMessage: () =>
                            infoDialog.show({
                                title: "Grouped Measure Unavailable",
                                content: `The grouped measure option is not available because there is a group field selected. To create a grouped measure joined with an "AND" first remove the group field.`,
                            }),
                        info: (
                            <span>
                                The <b>OR</b> option will create a separate axis on the chart. You may control how multiple axes display in the Display tab, Multiple Axis Display
                                option.
                            </span>
                        ),
                        direction: "top",
                    });
                })(),
        ],
        [
            "groupField",
            ({infoDialog, tileFields}, next) => {
                return SingleFieldWell({
                    ...formToSingleFieldWell(form, ["groupField"]),
                    hasDateGroupsConfiguration: hasDateGroupsConfiguration(tile.value.$type),
                    $type: "CategoryTileField",
                    label: "Group",
                    filterColumn: (column) => column.$type !== "AggregatedMeasureModelColumn",
                    onUpdateTile: (newVal, oriVal) =>
                        updateSingleFieldInTile({
                            fieldAttr: "groupField",
                            fieldVal: {newVal, oriVal},
                            tileFields,
                            tile,
                            updateSorting: getGroupSortingUpdated,
                            updateTileActions: getTileActionsUpdated,
                        }),
                    form,
                    next,
                    unavailable: () => tile.value?.yAxisFields?.filter((f) => f.measureFields?.length > 1).length > 0,
                    showUnavailableMessage: () =>
                        infoDialog.show({
                            title: "Grouped Field Unavailable",
                            content: `This chart has more than one measure (y-axis field) joined with an "AND" so a group field cannot be applied. To add a group field first update the y-axis fields so there no measures joined by an "AND"`,
                        }),
                });
            },
        ],
        [
            "fieldsSection",
            ({xAxisField, yAxisFields, groupField}, next) =>
                next({
                    render: () =>
                        Expandable({
                            label: "Fields",
                            render: () => (
                                <>
                                    {xAxisField.render()}
                                    {yAxisFields.render()}
                                    {groupField.render()}
                                </>
                            ),
                        }),
                    override: xAxisField.override || yAxisFields.override || groupField.override,
                }),
        ],

        [
            "limitSorting",
            ({tileFields}, next) =>
                cs(
                    [
                        "limit",
                        (_, next) => {
                            const formField = form.field(["limit"]);

                            return next(
                                Limit({
                                    allFields: tileFields.allFieldsWithoutUniq,
                                    field: formField.state,
                                    onChange: (newLimit, field) => {
                                        const limit = {
                                            ...newLimit,
                                            direction: field.$type === "MeasureTileField" ? "Asc" : newLimit.direction,
                                        };

                                        updateLimitSortingInTile({
                                            tile,
                                            limit,
                                            updateSorting: () =>
                                                getAxisSortingUpdatedByLimit({
                                                    tile,
                                                    field: {
                                                        ...limit,
                                                        $type: field.$type,
                                                    },
                                                    dimensionFieldAttr: "xAxisField",
                                                }),
                                        });
                                    },
                                    isHiddenRankOption: () => getPath(formField.state.value, ["modelColumnID"]) == getPath(tile.value, ["xAxisField", "modelColumnID"]),
                                })
                            );
                        },
                    ],
                    [
                        "sorting",
                        (_, next) =>
                            next(
                                <>
                                    {AxisSorting({
                                        fieldGroups: form.field(["sort"]).state,
                                        title: "X-Axis",
                                    })}
                                    {tile.value.groupField && GroupSorting({form, tile})}
                                </>
                            ),
                    ],
                    ({limit, sorting}) =>
                        next({
                            render: () =>
                                Expandable({
                                    initExpand: false,
                                    label: "Limit & Sorting",
                                    render: () => (
                                        <>
                                            {limit}
                                            {sorting}
                                        </>
                                    ),
                                }),
                        })
                ),
        ],

        ({limitSorting, fieldsSection}) =>
            next({
                render: () => {
                    return (
                        <div className="tile-fields-3vr">
                            {fieldsSection.render()}
                            {limitSorting.render()}
                            {tileActions?.render?.()}
                            {isSubTile && debugFunction?.render?.({})}
                        </div>
                    );
                },
                hasError: !form.group("fields").looksValid,
                override: fieldsSection.override || debugFunction?.override() || tileActions?.override({fieldType: "MeasureTileField"}),
                overrideAll: debugFunction?.overrideAll || tileActions?.overrideAll,
            })
    );
